export type BatteryStandardStatus = 'unknown' | 'low' | 'normal' | 'good' | 'full';
export type WorkerBatteryStatus = 'off' | 'normal' | 'low' | 'good';

export type BatteryLike = {
  volt?: number | string | null;
  percent?: number | string | null;
  level?: number | string | null;
  state?: string | null;
  status?: string | null;
  name?: string | null;
  isLowBattery?: boolean | null;
  is_low_battery?: boolean | null;
};

const BATTERY_STATUS_VALUES = new Set<BatteryStandardStatus>(['unknown', 'low', 'normal', 'good', 'full']);

function isBatteryStatus(value: unknown): value is BatteryStandardStatus {
  return typeof value === 'string' && BATTERY_STATUS_VALUES.has(value as BatteryStandardStatus);
}

function readNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim().length > 0) {
    const numericValue = Number(value.trim());
    if (Number.isFinite(numericValue)) {
      return numericValue;
    }
  }

  return null;
}

function isUnknownBatteryText(value: unknown): boolean {
  if (value == null) {
    return true;
  }

  const normalized = String(value).trim().toLowerCase();
  return !normalized || normalized.includes('unknown') || normalized.includes('n/a') || normalized.includes('알 수 없음');
}

function statusFromPercent(value: unknown): BatteryStandardStatus | null {
  const percent = readNumber(value);
  if (percent == null) {
    return null;
  }

  if (percent <= 20) {
    return 'low';
  }
  if (percent >= 95) {
    return 'full';
  }
  if (percent >= 60) {
    return 'good';
  }
  return 'normal';
}

function statusFromLevel(level: unknown, state: unknown): BatteryStandardStatus | null {
  if (state != null && isUnknownBatteryText(state)) {
    return 'unknown';
  }

  const numericLevel = readNumber(level);
  if (numericLevel == null) {
    return null;
  }
  if (numericLevel >= 4) {
    return 'full';
  }
  if (numericLevel >= 3) {
    return 'good';
  }
  if (numericLevel >= 2) {
    return 'normal';
  }
  if (numericLevel >= 0) {
    return 'low';
  }

  return null;
}

function statusFromState(value: unknown): BatteryStandardStatus | null {
  if (value == null) {
    return null;
  }
  if (isUnknownBatteryText(value)) {
    return 'unknown';
  }

  const normalized = String(value).trim().toLowerCase().replace(/\s+/g, '');
  if (normalized.includes('full')) {
    return 'full';
  }
  if (normalized.includes('-5%') || normalized.includes('5~35') || normalized.includes('저전력') || normalized.includes('매우낮') || normalized.includes('낮음')) {
    return 'low';
  }
  if (normalized.includes('35~65') || normalized.includes('보통')) {
    return 'normal';
  }
  if (normalized.includes('65~95') || normalized.includes('좋음')) {
    return normalized.includes('매우좋') ? 'full' : 'good';
  }

  return null;
}

function usesTwelveVoltRule(batt: BatteryLike, volt: number): boolean {
  const name = String(batt.name ?? '').trim();
  return volt > 5 || name === '위험알리미' || name.includes('위험알리미 G') || name.includes('G+') || name.includes('Dx');
}

function statusFromVoltage(batt: BatteryLike): BatteryStandardStatus | null {
  const volt = readNumber(batt.volt);
  if (volt == null || volt <= 0) {
    return 'unknown';
  }

  if (usesTwelveVoltRule(batt, volt)) {
    return volt < 11.0 ? 'low' : 'normal';
  }

  if (volt >= 4.1) {
    return 'full';
  }
  if (volt >= 3.8) {
    return 'good';
  }
  if (volt >= 3.6) {
    return 'normal';
  }
  return 'low';
}

function explicitStatus(batt: BatteryLike | null | undefined): BatteryStandardStatus | null {
  return isBatteryStatus(batt?.status) ? batt.status : null;
}

function hasLowBatteryFlag(batt: BatteryLike | null | undefined): boolean {
  return batt?.isLowBattery === true || batt?.is_low_battery === true;
}

function statusToDisplayPercent(status: BatteryStandardStatus): number | undefined {
  if (status === 'unknown') {
    return undefined;
  }
  if (status === 'low') {
    return 10;
  }
  if (status === 'normal') {
    return 50;
  }
  if (status === 'good') {
    return 80;
  }
  return 100;
}

/**
 * central-iot 배터리 계약과 같은 우선순위로 표준 상태를 결정합니다.
 */
export function resolveBatteryStandardStatus(batt: BatteryLike | null | undefined): BatteryStandardStatus {
  if (!batt) {
    return 'unknown';
  }
  if (hasLowBatteryFlag(batt)) {
    return 'low';
  }

  return (
    explicitStatus(batt) ??
    statusFromPercent(batt.percent) ??
    statusFromLevel(batt.level, batt.state) ??
    statusFromState(batt.state) ??
    statusFromState(batt.percent) ??
    statusFromVoltage(batt) ??
    'unknown'
  );
}

/**
 * 기존 지도/위젯 게이지가 사용할 표시용 퍼센트 값을 계산합니다.
 */
export function resolveBatteryDisplayPercent(batt: BatteryLike | null | undefined): number | undefined {
  if (!batt) {
    return undefined;
  }

  const percent = readNumber(batt.percent);
  const volt = readNumber(batt.volt);
  // 초기값성 0%와 유효하지 않은 전압은 기존 상태 fallback을 계속 확인합니다.
  if (percent != null && !(percent === 0 && (volt === 0 || volt == null))) {
    return Math.max(0, Math.min(100, Math.round(percent)));
  }

  if (hasLowBatteryFlag(batt)) {
    return statusToDisplayPercent('low');
  }

  const status = explicitStatus(batt);
  if (status) {
    return statusToDisplayPercent(status);
  }

  const level = readNumber(batt.level);
  if (level != null && !(level === 0 && isUnknownBatteryText(batt.state))) {
    return Math.max(0, Math.min(100, Math.round((level / 4) * 100)));
  }

  return statusToDisplayPercent(statusFromState(batt.state) ?? statusFromState(batt.percent) ?? statusFromVoltage(batt) ?? 'unknown');
}

/**
 * 근로자 리스트의 기존 off/low/normal/good 상태로 변환합니다.
 */
export function resolveWorkerBatteryStatus(batt: BatteryLike | null | undefined): WorkerBatteryStatus {
  const status = resolveBatteryStandardStatus(batt);
  if (status === 'unknown') {
    return 'off';
  }
  if (status === 'full') {
    return 'good';
  }
  return status;
}
